/**
 * EasterEggs — long-running app-wide listeners that surface delight without
 * cluttering normal UI. All effects are idle on cold load (no DOM cost
 * beyond the listeners + a single Snackbar that's only mounted when toast
 * text is non-empty).
 *
 * Triggers:
 *   - Console banner — printed once per session on mount (sherlock.help()).
 *   - Konami code   — ↑↑↓↓←→←→BA → 30s heavy-CRT mode.
 *   - Triple-click  — three clicks on the sidebar/topbar logo within 600ms
 *                     spawns a tiny detective walking across the screen.
 *   - cmd+K hidden  — /help, /elementary, /sudo, /coffee, /matrix, /whoami
 *                     → toast.
 *   - sherlock:toast — any component can dispatch it.
 */

import { Box, Snackbar } from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

const KONAMI = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
];

declare global {
  interface Window {
    sherlock?: {
      help: () => void;
      easterEggs: () => void;
      theme: { toggleScanlines: () => void; heavyCRT: () => void };
    };
  }
}

export function EasterEggs(): ReactElement {
  const [walkerActive, setWalkerActive] = useState(false);
  const [heavyCRT, setHeavyCRT] = useState(false);
  const [crtCountdown, setCrtCountdown] = useState(30);
  const [toast, setToast] = useState<string>('');
  const tripleClickRef = useRef<{ count: number; timer?: number }>({ count: 0 });
  const konamiBufferRef = useRef<string[]>([]);

  useEffect(() => {
    // Console banner — once per session
    if (typeof console !== 'undefined' && !sessionStorage.getItem('sherlock.banner')) {
      // eslint-disable-next-line no-console
      console.log(
        '%c🕵🏽 sherlock\n%cLooking under the hood? Try sherlock.help()',
        'color:#D97757;font-weight:700;font-family:monospace;font-size:14px;',
        'color:#c9c0ad;font-family:monospace;',
      );
      try {
        sessionStorage.setItem('sherlock.banner', '1');
      } catch {
        /* swallow */
      }
    }

    function triggerHeavyCRT(): void {
      setHeavyCRT(true);
      setCrtCountdown(30);
      const interval = window.setInterval(() => {
        setCrtCountdown((c) => {
          if (c <= 1) {
            window.clearInterval(interval);
            setHeavyCRT(false);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    }

    // Window globals — discoverable from devtools
    window.sherlock = {
      help: () => {
        // eslint-disable-next-line no-console
        console.log(
          'sherlock.theme.toggleScanlines()  — turn the CRT scanlines on/off\n' +
            'sherlock.theme.heavyCRT()         — 30s heavy-CRT mode\n' +
            'sherlock.easterEggs()              — list all eggs\n\n' +
            'In-app: cmd+K → /help, /elementary, /sudo, /coffee, /matrix, /whoami\n' +
            'Konami code: ↑↑↓↓←→←→BA',
        );
      },
      easterEggs: () => {
        // eslint-disable-next-line no-console
        console.log(
          [
            '· Triple-click the logo: a tiny detective walks across the screen',
            '· Konami code: 30s deep CRT mode',
            '· cmd+K hidden commands: /help, /elementary, /sudo, /coffee, /matrix, /whoami',
            '· Resolved critical issues stamp "CASE CLOSED"',
            "· Holmes' birthday (Jan 6): version flips to 'since 1854'",
          ].join('\n'),
        );
      },
      theme: {
        toggleScanlines: () => {
          const cur = localStorage.getItem('sherlock.scanlines');
          const next = cur === 'off' ? 'on' : 'off';
          localStorage.setItem('sherlock.scanlines', next);
          window.dispatchEvent(
            new CustomEvent('sherlock:scanlines', { detail: { enabled: next === 'on' } }),
          );
        },
        heavyCRT: () => triggerHeavyCRT(),
      },
    };

    function onKey(e: KeyboardEvent): void {
      const buf = konamiBufferRef.current;
      buf.push(e.key);
      if (buf.length > KONAMI.length) buf.shift();
      const matches =
        buf.length === KONAMI.length &&
        buf.every((k, i) => k.toLowerCase() === (KONAMI[i] ?? '').toLowerCase());
      if (matches) {
        triggerHeavyCRT();
        konamiBufferRef.current = [];
      }
    }

    function onClick(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      const isLogo = target?.closest('a[href="/insights"]');
      if (!isLogo) {
        tripleClickRef.current.count = 0;
        return;
      }
      tripleClickRef.current.count += 1;
      if (tripleClickRef.current.timer)
        window.clearTimeout(tripleClickRef.current.timer);
      tripleClickRef.current.timer = window.setTimeout(() => {
        tripleClickRef.current.count = 0;
      }, 600);
      if (tripleClickRef.current.count === 3) {
        setWalkerActive(true);
        window.setTimeout(() => setWalkerActive(false), 4500);
        tripleClickRef.current.count = 0;
      }
    }

    function onHidden(e: Event): void {
      const detail = (e as CustomEvent<{ name: string }>).detail;
      switch (detail.name) {
        case 'help':
          setToast('try /elementary, /sudo, /coffee, /matrix, /whoami');
          break;
        case 'elementary':
          setToast('Elementary, my dear Watson.');
          break;
        case 'sudo':
          setToast('nice try.');
          break;
        case 'coffee':
          setToast('☕ brewing… ASCII coffee not yet implemented');
          break;
        case 'matrix':
          setToast('🟧 matrix not yet implemented (room for future stretch)');
          break;
        case 'whoami':
          setToast('mohit@detect · eng · admin');
          break;
        case 'mute':
          try {
            localStorage.setItem('sherlock.buddy.muted', 'true');
          } catch { /* swallow */ }
          window.dispatchEvent(
            new CustomEvent('sherlock:buddy-mute', { detail: { muted: true } }),
          );
          setToast("🕵🏽 fine, I'll be quiet");
          break;
        case 'unmute':
          try {
            localStorage.removeItem('sherlock.buddy.muted');
          } catch { /* swallow */ }
          window.dispatchEvent(
            new CustomEvent('sherlock:buddy-mute', { detail: { muted: false } }),
          );
          setToast('🕵🏽 missed me?');
          break;
        default:
          break;
      }
    }

    function onToast(e: Event): void {
      const detail = (e as CustomEvent<{ message: string }>).detail;
      setToast(detail.message);
    }

    window.addEventListener('keydown', onKey);
    window.addEventListener('click', onClick);
    window.addEventListener('sherlock:hidden-command', onHidden as EventListener);
    window.addEventListener('sherlock:toast', onToast as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', onClick);
      window.removeEventListener('sherlock:hidden-command', onHidden as EventListener);
      window.removeEventListener('sherlock:toast', onToast as EventListener);
    };
  }, []);

  return (
    <>
      {walkerActive && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            bottom: 8,
            left: 0,
            zIndex: 50,
            fontSize: 24,
            pointerEvents: 'none',
            animation: 'walkacross 4s linear forwards',
            '@keyframes walkacross': {
              '0%': { transform: 'translateX(-40px)' },
              '100%': { transform: 'translateX(calc(100vw + 40px))' },
            },
          }}
        >
          🕵🏽
        </Box>
      )}

      {heavyCRT && (
        <>
          <Box
            aria-hidden="true"
            sx={{
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 5,
              backgroundImage:
                'repeating-linear-gradient(0deg, rgba(0,0,0,0.32) 0px, rgba(0,0,0,0.32) 1px, transparent 1px, transparent 2px), radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.6) 100%)',
              mixBlendMode: 'multiply',
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              right: 12,
              bottom: 12,
              zIndex: 60,
              backgroundColor: 'background.deeper',
              border: 1,
              borderColor: 'primary.main',
              fontFamily: '"JetBrains Mono", monospace',
              color: 'primary.main',
              px: 1.5,
              py: 0.75,
              fontSize: 11,
              borderRadius: 1,
              textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
            }}
          >
            ▓ HEAVY CRT 0:{String(crtCountdown).padStart(2, '0')}
          </Box>
        </>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <Box
            sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.primary' }}
          >
            {toast}
          </Box>
        }
        ContentProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'primary.main',
            boxShadow: '0 0 8px rgba(217, 119, 87, 0.55)',
          },
        }}
      />
    </>
  );
}
